/**
 * GSAP Animation Engine - Unified Master
 */

const initGsapEngine = () => {
  if (typeof gsap === 'undefined') return;

  const body = document.body;
  const triggerType = body.getAttribute('data-gsap-trigger') || 'once';
  const globalEase = body.getAttribute('data-gsap-ease') || 'power4.out';
  const globalDuration = parseFloat(body.getAttribute('data-gsap-duration')) || 1.0;
  const globalStagger = parseFloat(body.getAttribute('data-gsap-stagger')) / 1000 || 0.03;

  const tagMap = {
    'H1': 'h1', 'H2': 'h2', 'H3': 'h3', 'H4': 'h4', 'H5': 'h5', 'H6': 'h6',
    'P': 'p', 'SPAN': 'sub', 'DIV': 'sub'
  };

  const elements = document.querySelectorAll('h1, h2, h3, h4, h5, h6, p, .subheading, .accent-subtitle');

  elements.forEach(el => {
    if (el.classList.contains('gsap-init') || el.closest('footer') || el.innerText.trim().length < 2) return;

    const tagKey = tagMap[el.tagName] || (el.classList.contains('subheading') ? 'sub' : null);
    if (!tagKey) return;

    const animType = body.getAttribute(`data-gsap-${tagKey}-type`);
    const animDir = body.getAttribute(`data-gsap-${tagKey}-dir`) || 'up';

    if (animType === 'none' || !animType) return;

    // --- WORD-AWARE SPLITTING (Fixes orphaned letters) ---
    const originalText = el.innerText;
    const words = originalText.split(' ');
    
    el.innerHTML = words.map(word => {
      const chars = word.split('').map(char => 
        `<span class="gsap-reveal-mask"><span class="gsap-char">${char}</span></span>`
      ).join('');
      return `<span class="gsap-word">${chars}</span>`;
    }).join(' ');

    const chars = el.querySelectorAll('.gsap-char');
    const tl = gsap.timeline({ paused: true });

    const moveProps = {
      'up':    { y: "115%", x: 0 },
      'down':  { y: "-115%", x: 0 },
      'left':  { x: "105%", y: 0 },
      'right': { x: "-105%", y: 0 }
    };

    let fromVars = { duration: globalDuration, ease: globalEase, stagger: globalStagger };

    if (animType === 'reveal') {
      Object.assign(fromVars, moveProps[animDir], { opacity: 1 });
    } else if (animType === 'fade') {
      const drift = 40;
      const fadeMap = { 'up': {y:drift}, 'down': {y:-drift}, 'left': {x:drift}, 'right': {x:-drift} };
      Object.assign(fromVars, fadeMap[animDir], { opacity: 0 });
    } else if (animType === 'blur') {
      Object.assign(fromVars, { filter: "blur(10px)", opacity: 0, y: 10 });
    }

    tl.from(chars, fromVars);
    gsap.set(el, { opacity: 1 });

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          tl.play();
          if (triggerType === 'once') observer.unobserve(entry.target);
        } else {
          if (triggerType === 'repeat' || el.closest('.slideshow, .slider')) {
            tl.pause(0); 
          }
        }
      });
    }, { threshold: 0.15 });

    observer.observe(el);
    el.classList.add('gsap-init');
  });
};

const initSectionBlur = () => {
  if (typeof gsap === 'undefined') return;

  const targets = document.querySelectorAll('[data-gsap-blur-target]');

  targets.forEach(target => {
    const runReveal = (el) => {
      // Use fromTo to force it to be blurry at the start of the animation
      gsap.fromTo(el, 
        { filter: "blur(1.5rem)" }, 
        { 
          filter: "blur(0px)",
          duration: 1.8, 
          ease: "sine.out",
          overwrite: "auto",
          // We removed clearProps so the 0px "sticks"
        }
      );
    };

    const slideParent = target.closest('slideshow-slide');

    if (slideParent) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.attributeName === 'aria-hidden') {
            const isVisible = slideParent.getAttribute('aria-hidden') === 'false';
            if (isVisible) {
              runReveal(target);
            }
          }
        });
      });
      observer.observe(slideParent, { attributes: true });

      if (slideParent.getAttribute('aria-hidden') === 'false') {
        runReveal(target);
      }
    } else {
      const sectionWrapper = target.closest('.shopify-section, .section');
      ScrollTrigger.create({
        trigger: sectionWrapper,
        start: "top 85%",
        onEnter: () => runReveal(target)
      });
    }

    target.classList.add('gsap-init-blur');
  });
};

/**
 * THE EXECUTION HUB
 */
const runAnimationSuite = () => {
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
        if (!window.gsapRetryCount) window.gsapRetryCount = 0;
        if (window.gsapRetryCount < 10) {
            window.gsapRetryCount++;
            setTimeout(runAnimationSuite, 50);
        }
        return;
    }

    gsap.registerPlugin(ScrollTrigger);
    initGsapEngine();
    initSectionBlur();
};

// LISTENERS (Only one of each!)
document.addEventListener('DOMContentLoaded', runAnimationSuite);

document.addEventListener('shopify:section:load', () => {
    setTimeout(runAnimationSuite, 50);
    if (window.ScrollTrigger) ScrollTrigger.refresh();
});